import {
  type EdgeData,
  type GraphEdgesSettings,
  type NodeId,
  type NodeRecord,
  dataForPositionsFromEdge,
  getEdgePositions,
} from '@ipfabric/utils';
import { mergeRecords } from '@ipfabric/utils/fp/mergeRecords';
import { prop } from '@ipfabric/utils/fp/prop';
import { createCollapseGroupEdges } from 'components/Graph/commands/collapsing/helpers/createCollapseGroupEdges';
import { destroyEdgesByNodes } from 'components/Graph/commands/collapsing/helpers/destroyEdgesByNodes';
import { putEdgeToGraphDraft } from 'components/Graph/commands/collapsing/helpers/putEdgeToGraphDraft';
import { removeNodeFromState } from 'components/Graph/commands/helpers/removeNodeFromState';
import { buildNodesEdgesMap } from 'components/Graph/helpers/buildNodesEdgesMap';
import {
  type CollapsedNodeGroupId,
  type GraphData,
  type HydratedCollapsedNodeGroup,
  collapsedNodeGroupIdFromNode,
} from 'components/Graph/model/GraphData';
import { array, record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/lib/function';
import { type Draft, produce } from 'immer';

const getHiddenNodesAfterUncollapse = (
  groupIdsToUncollapse: Set<CollapsedNodeGroupId>,
  graph: GraphData,
): Set<NodeId> => {
  return new Set(
    Array.from(graph.hiddenNodes).reduce(
      (nodeIds: NodeId[], nodeId: NodeId) => {
        const groupId = collapsedNodeGroupIdFromNode(nodeId);
        if (groupIdsToUncollapse.has(groupId)) {
          nodeIds.push(
            // Fixed noUncheckedIndexedAccess NIM-19408
            ...record.keys(graph.collapsedNodeGroups[groupId]!.nodes),
          );
          return nodeIds;
        }

        nodeIds.push(nodeId);
        return nodeIds;
      },
      [],
    ),
  );
};

const getNodesAfterUncollapse = (
  groupsToUncollapse: HydratedCollapsedNodeGroup[],
  graph: GraphData,
): NodeRecord => {
  return groupsToUncollapse.reduce((nodes, group) => {
    const groupNode = nodes[group.id];

    if (!groupNode) {
      return nodes;
    }

    return {
      // biome-ignore lint/performance/noAccumulatingSpread: would need bigger refactoring
      ...nodes,
      ...pipe(
        group.nodes,
        record.map((node) => ({
          ...node,
          // NOTE: When uncollapsing a group, we move all nodes in the group according to the position
          // delta of the group. `originalPosition` is set when the group is created/loaded.
          position: {
            x:
              node.position.x + groupNode.position.x - group.originalPosition.x,
            y:
              node.position.y + groupNode.position.y - group.originalPosition.y,
          },
        })),
      ),
    };
  }, graph.nodes);
};

/**
 * This function relies on being executed after `getNodesAfterUncollapse` since it inspects
 * what nodes will be uncollapsed now and rebuilds collapsed edges for edges that lead
 * to still collapsed nodes.
 */
const uncollapseEdges = (
  groupIdsToUncollapse: Set<CollapsedNodeGroupId>,
  groupsToUncollapse: HydratedCollapsedNodeGroup[],
  draft: Draft<GraphData>,
  edgesSettings: GraphEdgesSettings,
): void => {
  const isEdgeBetweenRegularNodes = (edge: EdgeData): boolean =>
    !!(draft.nodes[edge.source] && draft.nodes[edge.target]);

  const repositionEdge = (edge: EdgeData): EdgeData => ({
    ...edge,
    positions: getEdgePositions(
      // Fixed noUncheckedIndexedAccess NIM-19408
      draft.nodes[edge.source]!,
      draft.nodes[edge.target]!,
      dataForPositionsFromEdge(edge),
    ),
  });

  // remove collapse group edges leading to groups that are being uncollapsed
  destroyEdgesByNodes(draft, groupIdsToUncollapse);

  const { left: edgesToRecollapse, right: regularEdges } = pipe(
    groupsToUncollapse,
    array.map(prop('edges')),
    mergeRecords,
    Object.values,
    array.partition(isEdgeBetweenRegularNodes),
  );

  regularEdges.forEach(flow(repositionEdge, putEdgeToGraphDraft(draft)));

  pipe(
    edgesToRecollapse,
    createCollapseGroupEdges(draft, edgesSettings),
    array.map(putEdgeToGraphDraft(draft)),
  );
};

export const uncollapseNodes = (
  graph: GraphData,
  edgesSettings: GraphEdgesSettings,
  groupIdsToUncollapse: Set<CollapsedNodeGroupId>,
): GraphData => {
  const groupsToUncollapse = Object.values(graph.collapsedNodeGroups).filter(
    (group) => groupIdsToUncollapse.has(group.id),
  );

  const uncollapsedNodeIds = groupsToUncollapse.reduce(
    (acc: NodeId[], val: HydratedCollapsedNodeGroup): NodeId[] => {
      acc.push(...record.keys(val.nodes));
      return acc;
    },
    [],
  );

  return produce(graph, (draft) => {
    draft.hiddenNodes = getHiddenNodesAfterUncollapse(
      groupIdsToUncollapse,
      draft,
    );
    draft.nodes = getNodesAfterUncollapse(groupsToUncollapse, draft);

    groupIdsToUncollapse.forEach(removeNodeFromState(draft));

    for (const groupId of groupIdsToUncollapse) {
      delete draft.collapsedNodeGroups[groupId];
    }

    uncollapseEdges(
      groupIdsToUncollapse,
      groupsToUncollapse,
      draft,
      edgesSettings,
    );
    draft.nodesEdgesMap = buildNodesEdgesMap(draft.nodes, draft.edges);

    // Select ungrouped nodes
    for (const nodeId of uncollapsedNodeIds) {
      draft.selectedNodeIds.add(nodeId);
    }
  });
};
